import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Typography,
  Paper,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import type { PolicyTroubleshootingView } from '../../utils/policyTroubleshooting';
import type { PresentedPolicy } from '../../domain/policies/policyPresentation';
import { resolveInvestigationAlignmentFromPolicyConfig } from '../../domain/reportContext/investigationAlignment';
import { resolveClaimsRoiFromPolicyConfig } from '../../domain/reportContext/claimsRoiAlignment';
import { resolveSafetyControlReviewFromPolicyConfig } from '../../domain/reportContext/safetyControlReview';
import { buildReportContextForAlias, readPolicyUiContext } from '../../domain/policies/policyPacks';
import { CONFIG_SCOPE_LABELS, CONFIG_STATE_LABELS } from '../../utils/configVocabulary';
import InvestigationAlignmentCard from '../safetyReports/InvestigationAlignmentCard';
import ClaimsRoiAlignmentCard from '../safetyReports/ClaimsRoiAlignmentCard';
import SafetyControlReviewCard from '../safetyReports/SafetyControlReviewCard';
import PolicyScopeSummary from './PolicyScopeSummary';
import PolicyStatusBadge from './PolicyStatusBadge';
import type { StartMeasuringPresentation } from '../../domain/safetyCases/startMeasuring';

export interface PolicyDetailsDialogProps {
  open: boolean;
  view: PolicyTroubleshootingView | null;
  presented?: PresentedPolicy | null;
  policyTypeLabel: string;
  canWritePolicies: boolean;
  canDeployPolicies: boolean;
  canaryGroup: { id: string; name: string } | null;
  scopeCanary: number | null;
  scopeFull: number | null;
  onClose: () => void;
  onEdit: () => void;
  onOverride: () => void;
  onActivate: () => void;
  onDeactivate: () => void;
  onDeploy: (groupId?: string) => void;
  onDelete: () => void;
  onMeasure: () => void;
  onOpenDeployments: () => void;
  measurement?: StartMeasuringPresentation | null;
  measurementBusy?: boolean;
  measurementError?: string | null;
  onStartMeasuring?: () => void;
  onSaveForLater?: () => void;
}

const PolicyDetailsDialog: React.FC<PolicyDetailsDialogProps> = ({
  open,
  view,
  presented,
  policyTypeLabel,
  canWritePolicies,
  canDeployPolicies,
  canaryGroup,
  scopeCanary,
  scopeFull,
  onClose,
  onEdit,
  onOverride,
  onActivate,
  onDeactivate,
  onDeploy,
  onDelete,
  onMeasure,
  onOpenDeployments,
  measurement,
  measurementBusy,
  measurementError,
  onStartMeasuring,
  onSaveForLater,
}) => {
  const policy = view?.policy;
  if (!view || !policy) return null;

  const formatDateTime = (value?: string | null): string => {
    if (!value) return '—';
    const ts = Date.parse(value);
    if (!Number.isFinite(ts)) return value;
    return new Date(ts).toLocaleString();
  };

  const triggerLabel = presented?.policyLabel || policyTypeLabel;
  const scopeSummary = presented?.scopeSummary || view.lineSummary || view.zoneSummary || 'No scope';
  const deploymentReason = String(policy.deployment_reason || '').trim().toLowerCase();
  const deploymentSummary =
    view.state === 'healthy'
      ? (deploymentReason.includes('pending deploy') || deploymentReason.includes('newer changes pending')
          ? CONFIG_STATE_LABELS.needsAction
          : CONFIG_STATE_LABELS.deployed)
      : view.state === 'pending_deploy'
        ? CONFIG_STATE_LABELS.needsAction
        : view.state === 'deploying'
          ? CONFIG_STATE_LABELS.deploying
          : view.state === 'deployment_failed' || view.state === 'deployment_timeout'
            ? CONFIG_STATE_LABELS.blocked
            : (view.deploymentMeta.label || CONFIG_STATE_LABELS.unknown);
  const edgeDeviceLabel = view.targetDeviceId || CONFIG_SCOPE_LABELS.noEdgeDevice;
  const cameraLabel = presented?.cameraLabel || view.targetCameraId || CONFIG_SCOPE_LABELS.noCameraStream;
  const investigationAlignment = resolveInvestigationAlignmentFromPolicyConfig(policy.config);
  const claimsRoiAlignment = resolveClaimsRoiFromPolicyConfig(policy.config);
  const safetyControlReview = resolveSafetyControlReviewFromPolicyConfig(policy.config);
  const policyUiContext = readPolicyUiContext(policy);
  const configuredPolicyContext = (
    policy.config?.report_context && typeof policy.config.report_context === 'object'
      ? policy.config.report_context
      : buildReportContextForAlias(String(policyUiContext?.policy_alias || '').trim())
  ) as Record<string, unknown> | null;
  const formatContextToken = (value?: unknown): string =>
    String(value || '')
      .trim()
      .replace(/[_-]+/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase()) || '—';

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', md: 560 }, p: 2.5 } }}>
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" noWrap>
              {policy.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {policy.policy_id}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography variant="body2" color="text.secondary">
          Review scope, measurement readiness, deployment readiness, and troubleshooting details without leaving the main policy list.
        </Typography>

        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          <PolicyStatusBadge
            label={policy.status === 'active' && policy.enabled !== false ? CONFIG_STATE_LABELS.live : CONFIG_STATE_LABELS.draft}
          />
          <PolicyStatusBadge label={deploymentSummary} kind="deployment" />
          {presented ? <PolicyStatusBadge label={presented.packLabel} kind="pack" /> : null}
          {presented?.scopeChips?.[0] ? <PolicyStatusBadge label={presented.scopeChips[0]} kind="scope" /> : null}
        </Box>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
          <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Summary</Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: 1.25,
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">
                Status
              </Typography>
              <Typography variant="body2">{view.statusMeta.label}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Pack
              </Typography>
              <Typography variant="body2">{presented?.packLabel || 'Unassigned'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {CONFIG_SCOPE_LABELS.edgeDevice}
              </Typography>
              <Typography variant="body2">{edgeDeviceLabel}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {CONFIG_SCOPE_LABELS.cameraStream}
              </Typography>
              <Typography variant="body2">{cameraLabel}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Scope
              </Typography>
              <Typography variant="body2">{scopeSummary}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Policy alias
              </Typography>
              <Typography variant="body2">{String(policyUiContext?.policy_alias || '—')}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Deployment
              </Typography>
              <Typography variant="body2">{deploymentSummary}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Scan template
              </Typography>
              <Typography variant="body2">{formatContextToken(configuredPolicyContext?.scan_template)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Last updated
              </Typography>
              <Typography variant="body2">{formatDateTime(policy.updated_at || policy.created_at)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Exposure category
              </Typography>
              <Typography variant="body2">{formatContextToken(configuredPolicyContext?.exposure_category)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Last deployed
              </Typography>
              <Typography variant="body2">{formatDateTime(policy.last_deployed_at || policy.deployment?.published_at)}</Typography>
            </Box>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
          <Typography sx={{ fontWeight: 700, mb: 1.25 }}>Condition / trigger</Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {triggerLabel}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {policy.description || view.detail}
          </Typography>
        </Paper>

        <InvestigationAlignmentCard
          alignment={investigationAlignment}
          title="Investigation alignment"
          compact
          testId="policy-details-investigation-alignment"
        />

        <ClaimsRoiAlignmentCard
          alignment={claimsRoiAlignment}
          title="Claims / ROI alignment"
          compact
          testId="policy-details-claims-roi-alignment"
        />

        <SafetyControlReviewCard
          alignment={safetyControlReview}
          title="Safety control review"
          compact
          testId="policy-details-safety-control-review"
        />

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
          <Typography sx={{ fontWeight: 700, mb: 1.25 }}>Scope</Typography>
          <PolicyScopeSummary
            summary={scopeSummary}
            chips={presented?.scopeChips || []}
            showChips
          />
          <Box sx={{ mt: 1.25, display: 'grid', gap: 0.75 }}>
            <Typography variant="body2" color="text.secondary">
              {CONFIG_SCOPE_LABELS.edgeDevice}: {edgeDeviceLabel}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {CONFIG_SCOPE_LABELS.cameraStream}: {cameraLabel}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {CONFIG_SCOPE_LABELS.zone} / {CONFIG_SCOPE_LABELS.line} resolution: {view.lineSummary || view.zoneSummary || '—'}
            </Typography>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
          <Typography sx={{ fontWeight: 700, mb: 1.25 }}>Deployment</Typography>
          <Box sx={{ display: 'grid', gap: 0.75 }}>
            <Typography variant="body2">
              State: {view.deploymentMeta.label}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {view.deploymentLabel}
            </Typography>
            {(policy.deployment?.deployment_id || policy.last_deployed_at || policy.last_ack_at) ? (
              <Typography variant="body2" color="text.secondary">
                {policy.deployment?.deployment_id ? `Deployment ${policy.deployment.deployment_id}` : 'No deployment id'}
                {policy.last_deployed_at ? ` · Last deploy ${formatDateTime(policy.last_deployed_at)}` : ''}
                {policy.last_ack_at ? ` · ACK ${formatDateTime(policy.last_ack_at)}` : ''}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No deployment metadata available.
              </Typography>
            )}
            {(policy.deployment?.bundle_version != null ||
              policy.deployment?.bundle_hash ||
              policy.deployment?.published_at ||
              policy.deployment?.acked_at) && (
              <Typography variant="body2" color="text.secondary">
                {policy.deployment?.bundle_version != null ? `Version ${policy.deployment.bundle_version}` : 'Version unknown'}
                {policy.deployment?.bundle_hash ? ` · sha256:${String(policy.deployment.bundle_hash).slice(0, 12)}…` : ''}
                {policy.deployment?.published_at ? ` · Published ${formatDateTime(policy.deployment.published_at)}` : ''}
                {policy.deployment?.acked_at ? ` · ACK ${formatDateTime(policy.deployment.acked_at)}` : ''}
              </Typography>
            )}
          </Box>
        </Paper>

        {(view.issueList.length > 0 || policy.description) && (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
            <Typography sx={{ fontWeight: 700, mb: 1.25 }}>Problem details</Typography>
            {view.issueList.length > 0 ? (
              <List dense disablePadding sx={{ mb: policy.description ? 1.5 : 0 }}>
                {view.issueList.map((issue, idx) => (
                  <ListItem key={`${issue}-${idx}`} disableGutters>
                    <ListItemText primary={issue} />
                  </ListItem>
                ))}
              </List>
            ) : null}
            {policy.description ? (
              <Typography variant="body2" color="text.secondary">
                {policy.description}
              </Typography>
            ) : null}
          </Paper>
        )}

        {measurement ? (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }} data-testid="start-measuring-panel">
            <Typography sx={{ fontWeight: 700, mb: 1.25 }}>Measurement</Typography>
            {measurement.statusLabel ? (
              <Typography variant="body2" sx={{ mb: 1 }} data-testid="measurement-active-status">
                {measurement.statusLabel}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Start measuring this condition?
              </Typography>
            )}
            {measurement.missingMessage ? (
              <Typography variant="body2" color="error.main" sx={{ mb: 1 }} data-testid="start-measuring-missing">
                {measurement.missingMessage}
              </Typography>
            ) : null}
            {measurementError ? (
              <Typography variant="body2" color="error.main" sx={{ mb: 1 }} data-testid="start-measuring-error">
                {measurementError}
              </Typography>
            ) : null}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={onStartMeasuring}
                disabled={Boolean(measurementBusy || (measurement.mode === 'blocked'))}
                data-testid="start-measuring-cta"
              >
                {measurementBusy ? 'Starting…' : measurement.primaryLabel}
              </Button>
              {measurement.secondaryLabel ? (
                <Button
                  variant="outlined"
                  onClick={onSaveForLater}
                  disabled={measurementBusy}
                  data-testid="save-for-later-cta"
                >
                  {measurement.secondaryLabel}
                </Button>
              ) : null}
            </Box>
          </Paper>
        ) : null}

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
          <Typography sx={{ fontWeight: 700, mb: 1.25 }}>Actions</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button onClick={onMeasure} variant="outlined">
              Measure detection readiness
            </Button>
            <Button onClick={onEdit} variant="outlined" disabled={!canWritePolicies}>
              Edit policy
            </Button>
            <Button onClick={onOverride} variant="outlined" disabled={!canWritePolicies}>
              Override
            </Button>
            <Button onClick={onOpenDeployments} variant="outlined">
              Deployment history
            </Button>
            {policy.status === 'active' ? (
              <Button onClick={onDeactivate} variant="outlined" disabled={!canWritePolicies}>
                Deactivate
              </Button>
            ) : (
              <Button onClick={onActivate} variant="outlined" disabled={!canWritePolicies}>
                Activate
              </Button>
            )}
            {canaryGroup && scopeCanary != null && scopeFull != null ? (
              <>
                <Button
                  onClick={() => onDeploy(canaryGroup.id)}
                  variant="contained"
                  disabled={!canDeployPolicies || policy.status !== 'active' || policy.enabled === false}
                >
                  Deploy to canary ({scopeCanary})
                </Button>
                <Button
                  onClick={() => onDeploy()}
                  variant="outlined"
                  disabled={!canDeployPolicies || policy.status !== 'active' || policy.enabled === false}
                >
                  Deploy to full site ({scopeFull})
                </Button>
              </>
            ) : (
              <Button
                onClick={() => onDeploy()}
                variant="contained"
                disabled={!canDeployPolicies || policy.status !== 'active' || policy.enabled === false}
              >
                Deploy
              </Button>
            )}
            <Button onClick={onDelete} color="error" variant="contained" disabled={!canWritePolicies}>
              Delete
            </Button>
          </Box>
        </Paper>

        <Accordion disableGutters sx={{ '&:before': { display: 'none' }, borderRadius: '12px !important' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 700 }}>Technical metadata</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Raw identifiers, timestamps, deployment metadata, and troubleshooting payload.
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                bgcolor: 'grey.50',
                fontFamily: 'monospace',
                fontSize: 12,
                whiteSpace: 'pre-wrap',
                overflow: 'auto',
              }}
            >
              {JSON.stringify(
                {
                  policy,
                  presented,
                  troubleshooting: view,
                },
                null,
                2,
              )}
            </Paper>
          </AccordionDetails>
        </Accordion>
      </Box>
    </Drawer>
  );
};

export default PolicyDetailsDialog;
